import React, { useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { CircularProgress, Dialog, DialogContent, DialogTitle, IconButton, makeStyles, Typography } from '@material-ui/core';
import { Close } from '@material-ui/icons';
import { Timeline } from '@material-ui/lab';
import { useTranslation } from 'react-i18next';
import AuditContext from './AuditContext';
import IconMap from './icons';
import AuditDetail from './AuditDetail';
import API from '../../../api-rest-uruk';
import InfoAboutStep from './InfoAboutStep';

const useStyles = makeStyles(() => ({
  title: {
    '& h2': {
      display: 'flex',
      justifyContent: 'space-between'
    }
  },
  contentHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px'
  },
  contentBody: {
    padding: '24px 0'
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center'
  }
}));

const AuditStep = ({ flow }) => {
  const { flowId } = flow;
  const [open, setOpen] = useState(false);
  const [audit, setAudit] = useState(null);
  const [errorCode, setErrorCode] = useState(null);
  const { step, setStep } = useContext(AuditContext);
  const { t } = useTranslation();
  const classes = useStyles();

  useEffect(() => {
    setOpen(!!step);
    setAudit(null);
    setErrorCode(null);

    if (!step) return;
    API.getAudit(`${flowId}-${step.stepId}`)
      .then(res => {
        const audit = res.items;
        setAudit(audit);
      })
      .catch((err) => {
        if (err.response && err.response.status === 404) setErrorCode('notFound');
        else setErrorCode('error');
      });
  }, [step, flowId]);

  const handleClose = () => {
    setOpen(false);
    setStep(null);
  };
  if (!step) return null;

  return (
    <Dialog
      onClose={handleClose}
      maxWidth='xl'
      fullWidth
      open={open}
    >
      <DialogTitle className={classes.title}>
        <span>{t('stepDetailDialog:detailOf') + (step.order + 1)}</span>
        <IconButton aria-label='close' onClick={handleClose}>
          <Close />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Typography variant='h6' className={classes.contentHeader}>
          {IconMap[step.status]}
          <span>{step.alias || t(`processDetail:stepType:${step.type}`)}</span>
        </Typography>
        <div className={classes.contentBody}>
          <div>
            <Typography variant='body1'>
              <b>{t(`processDetail:stepDialog:status`)}</b>
              {t(`processDetail:${step.status}`)}
            </Typography>
          </div>
          <div>
            <Typography variant='body1'>
              <b>{t(`processDetail:stepDialog:stepId`)}</b>
              {step.stepId}
            </Typography>
          </div>
          {step.sendToSignAt &&
            <div>
              <Typography variant='body1'>
                <b>{t(`processDetail:stepDialog:sendToSign`)}</b>
                {new Date(step.sendToSignAt).toLocaleString()}
              </Typography>
            </div>
          }
          {step.signedAt &&
            <div>
              <Typography variant='body1'>
                <b>{t(`processDetail:stepDialog:signedAt`)}</b>
                {new Date(step.signedAt).toLocaleString()}
              </Typography>
            </div>
          }
          <InfoAboutStep type={step.type} props={step} flow={flow} />
          {(step.type !== 'qrSignStep') && (
            <Timeline align='alternate'>
              {!errorCode && !audit ? (
                <div className={classes.loadingContainer}>
                  <CircularProgress />
                </div>
              )
                : errorCode ? (
                  <div className={classes.loadingContainer}>
                    {t(`stepper:${errorCode}`)}
                  </div>
                )
                  : audit.map((item, i) => (
                    <AuditDetail audit={item} type={step.type} key={i} />
                  ))}
            </Timeline>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

AuditStep.propTypes = {
  flow: PropTypes.object.isRequired
};

export default AuditStep;
